import { Controller } from "@hotwired/stimulus"
import { renderStreamMessage } from "@hotwired/turbo"

// AttachmentsUploadController — the reusable half of MC-1091's attachment
// upload, generalised in MC-1092 so both the task drawer and the docs
// reading canvas share one implementation instead of two.
//
// It funnels two input routes into one upload:
//
//   - Drag and drop onto the drop surface, with a visible drop state,
//   - A hidden file input opened by the "Attach file" button.
//
// Each sends the file to `endpointValue` as multipart form data with the
// page's CSRF token, then applies the server's Turbo Stream response (which
// replaces the attachments section). The controller never builds rows in the
// DOM itself — the server owns that rendering.
//
// The upload half is parameterised by `endpointValue` alone, so a subclass
// only supplies its endpoint. `Tasks::AttachmentsRegionComponent` subclasses
// this to add paste and drawer-wide binding (a task screenshot pastes onto
// the whole drawer); `Docs::AttachmentsRegionComponent` uses it as-is —
// paste is deliberately not wired on a doc, whose editor textarea is where a
// paste naturally goes.
export default class extends Controller {
  static values = { endpoint: String, dragClass: String }
  static targets = ["input"]

  connect() {
    this.surface = this.dropSurface()

    this.onDragOver = this.dragOver.bind(this)
    this.onDragLeave = this.dragLeave.bind(this)
    this.onDrop = this.drop.bind(this)

    this.surface.addEventListener("dragover", this.onDragOver)
    this.surface.addEventListener("dragleave", this.onDragLeave)
    this.surface.addEventListener("drop", this.onDrop)
  }

  disconnect() {
    this.surface.removeEventListener("dragover", this.onDragOver)
    this.surface.removeEventListener("dragleave", this.onDragLeave)
    this.surface.removeEventListener("drop", this.onDrop)
    this.clearDragState()
  }

  // The element drag-and-drop binds to. This section by default; a subclass
  // overrides it to widen the surface (the task drawer binds to the whole
  // drawer body so a file can be dropped anywhere on the task).
  dropSurface() {
    return this.element
  }

  // "Attach file" button → open the hidden file input.
  browse() {
    this.inputTarget.click()
  }

  chosen() {
    const file = this.inputTarget.files && this.inputTarget.files[0]
    if (file) this.upload(file, file.name)
    // Reset so re-picking the same file fires `change` again.
    this.inputTarget.value = ""
  }

  dragOver(event) {
    if (!this.carriesFiles(event.dataTransfer)) return
    // Required so `drop` fires at all.
    event.preventDefault()
    this.setDragState()
  }

  dragLeave(event) {
    // Ignore leaves into a descendant of the surface — only a real exit
    // clears the drop state.
    if (event.relatedTarget && this.surface.contains(event.relatedTarget)) {
      return
    }
    this.clearDragState()
  }

  drop(event) {
    if (!this.carriesFiles(event.dataTransfer)) return

    event.preventDefault()
    this.clearDragState()
    const file = event.dataTransfer.files[0]
    if (file) this.upload(file, file.name)
  }

  async upload(file, filename) {
    // Refuse a second upload while one is in flight (MC-1221): a slow upload
    // renders nothing but the pending row, so an impatient operator pastes
    // again — and `Tasks::CreateAttachment` does not replace on repost, so a
    // second upload would land a duplicate. Guard, don't queue — the goal is
    // stopping the duplicate, not landing every drop. The server's Turbo
    // Stream replaces this frame (success or rejection), destroying and
    // reconnecting the controller, so `uploading` resets with no teardown.
    if (this.uploading) return
    this.uploading = true

    const pending = this.showPending(filename)

    try {
      const body = new FormData()
      body.append("file", file, filename)

      const response = await fetch(this.endpointValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": this.csrfToken()
        },
        body
      })

      // Only a Turbo Stream replaces the frame (and so resets the controller).
      // A dropped connection throws out of `fetch`; a non-turbo response — a
      // 413 from the proxy, a 500 error page, an empty body — is not a stream.
      // Both leave the frame in place, so route them to the failure path below
      // rather than letting the pending state stick forever (MC-1221 review).
      // Key on Content-Type alone, NOT `response.ok`: a rejected file (too
      // large, wrong type) answers 422 *with* a Turbo Stream that re-renders
      // the section carrying the server's specific error, and that stream must
      // render so the operator sees why — success is 201, rejection is 422,
      // both are streams (MC-1221 review, AC3).
      const contentType = response.headers.get("Content-Type") || ""
      const isStream = contentType.includes("text/vnd.turbo-stream.html")
      const stream = isStream ? await response.text() : ""
      if (!stream.trim()) throw new Error(`upload failed (${response.status})`)

      // The stream replaces this frame, tearing down and reconnecting the
      // controller — `uploading` and the pending row reset with no teardown.
      renderStreamMessage(stream)
    } catch {
      this.uploadFailed(pending)
    }
  }

  // Recover from an upload whose response never replaced the frame (a dropped
  // connection or a non-turbo response). The frame is still in place, so the
  // controller won't reconnect to reset itself: clear the in-flight guard so
  // paste, drop and browse work again, and swap the pending row for an inline
  // error — the client-side counterpart of the server's error row, shown by
  // the same convention that the pending row mirrors its Ruby counterpart.
  uploadFailed(pending) {
    this.uploading = false
    if (!pending || !pending.isConnected) return

    pending.className =
      "attachments-pending-error flex items-center gap-2 text-sm text-danger"
    pending.setAttribute("role", "alert")
    pending.setAttribute("aria-busy", "false")
    pending.removeAttribute("aria-live")
    const message = document.createElement("span")
    message.className = "attachments-pending-name flex-1"
    message.textContent = "Upload failed — please try again."
    pending.replaceChildren(message)
  }

  // Build the pending row the moment an upload starts, so the operator sees
  // the file is uploading before the round trip returns. Inserted into this
  // controller's element (inside the attachments turbo-frame), so the
  // server's stream replacing the frame removes it automatically on success
  // and on a rejection alike — no separate teardown. It mirrors the row
  // `Tasks::AttachmentsRegionComponent#pending_filename` renders server-side
  // for the a11y sweep: same classes and ARIA, by convention (as the tag
  // editor builds its chips), not a shared partial.
  showPending(filename) {
    const row = document.createElement("div")
    row.className = "attachments-pending-row flex items-center gap-2 text-sm text-muted"
    row.setAttribute("role", "status")
    row.setAttribute("aria-live", "polite")
    row.setAttribute("aria-busy", "true")

    const spinner = document.createElement("span")
    spinner.className =
      "attachments-pending-spinner h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
    spinner.setAttribute("aria-hidden", "true")
    row.appendChild(spinner)

    const name = document.createElement("span")
    name.className = "attachments-pending-name flex-1 truncate"
    name.textContent = filename
    row.appendChild(name)

    this.element.appendChild(row)
    return row
  }

  setDragState() {
    if (this.hasDragClassValue) this.surface.classList.add(this.dragClassValue)
  }

  clearDragState() {
    if (this.hasDragClassValue) {
      this.surface.classList.remove(this.dragClassValue)
    }
  }

  carriesFiles(dataTransfer) {
    if (!dataTransfer) return false
    const types = dataTransfer.types
    return types && Array.prototype.indexOf.call(types, "Files") !== -1
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.getAttribute("content") : ""
  }
}
